import {
  isGlobalPrivacyControlEnabled,
  isPrivacyCategoryAllowed
} from '@/privacy/privacyPreferences'
import logger from '@utils/logger'
import {
  hasValidReleaseCampaignIdentity,
  isSafeReleaseCampaignPath
} from '@utils/releaseCampaign'

import api from './enhancedApiService'

const RELEASE_CAMPAIGN_ANALYTICS_ENDPOINT = '/analytics/ingest/release-campaign'

export const RELEASE_CAMPAIGN_ANALYTICS_EVENTS = Object.freeze({
  VIEWED: 'RELEASE_CAMPAIGN_VIEWED',
  CTA_CLICKED: 'RELEASE_CAMPAIGN_CTA_CLICKED'
})

export const RELEASE_CAMPAIGN_PLACEMENTS = Object.freeze({
  PUBLIC_RELEASE_NOTICE: 'public_release_notice',
  PUBLIC_HEADER: 'public_header',
  HOME_HERO: 'home_hero',
  HOME_FINAL_CTA: 'home_final_cta',
  FEATURES_HERO: 'features_hero',
  FEATURES_FINAL_CTA: 'features_final_cta',
  ABOUT_FINAL_CTA: 'about_final_cta',
  PRICING_PLAN_CTA: 'pricing_plan_cta',
  PRICING_FINAL_CTA: 'pricing_final_cta'
})

const VIEW_PLACEMENTS = new Set([
  RELEASE_CAMPAIGN_PLACEMENTS.PUBLIC_RELEASE_NOTICE
])
const CLICK_PLACEMENTS = new Set(Object.values(RELEASE_CAMPAIGN_PLACEMENTS))
const viewedCampaignPlacements = new Set()
const pendingCampaignPlacements = new Set()

function isInternalDestination(destination) {
  return isSafeReleaseCampaignPath(destination)
}

function canTrack(analyticsAllowed) {
  return (
    analyticsAllowed === true &&
    isPrivacyCategoryAllowed('analytics') &&
    !isGlobalPrivacyControlEnabled()
  )
}

function getCampaignMetadata(campaign) {
  if (!hasValidReleaseCampaignIdentity(campaign)) return null

  return {
    campaignId: campaign.id,
    stage: campaign.stage,
    status: campaign.status
  }
}

async function postReleaseCampaignEvent(eventType, metadata) {
  try {
    await api.post(RELEASE_CAMPAIGN_ANALYTICS_ENDPOINT, {
      eventType,
      metadata
    })
    return true
  } catch (error) {
    logger.error('Release campaign analytics request failed', error)
    return false
  }
}

export function trackReleaseCampaignView({
  analyticsAllowed,
  campaign,
  placement
}) {
  if (!canTrack(analyticsAllowed) || !VIEW_PLACEMENTS.has(placement)) return

  const campaignMetadata = getCampaignMetadata(campaign)
  if (!campaignMetadata) return

  const dedupeKey = [
    campaignMetadata.campaignId,
    campaignMetadata.stage,
    campaignMetadata.status,
    placement
  ].join(':')

  if (
    viewedCampaignPlacements.has(dedupeKey) ||
    pendingCampaignPlacements.has(dedupeKey)
  ) {
    return
  }
  pendingCampaignPlacements.add(dedupeKey)

  void postReleaseCampaignEvent(RELEASE_CAMPAIGN_ANALYTICS_EVENTS.VIEWED, {
    ...campaignMetadata,
    placement
  }).then(succeeded => {
    pendingCampaignPlacements.delete(dedupeKey)
    if (succeeded) {
      viewedCampaignPlacements.add(dedupeKey)
    }
  })
}

export function trackReleaseCampaignCtaClick({
  analyticsAllowed,
  campaign,
  destination,
  placement
}) {
  if (!canTrack(analyticsAllowed) || !CLICK_PLACEMENTS.has(placement)) return

  const campaignMetadata = getCampaignMetadata(campaign)
  if (!campaignMetadata) return
  if (
    !isInternalDestination(destination) ||
    destination !== campaign.primaryCta?.path
  ) {
    return
  }

  const metadata = {
    ...campaignMetadata,
    placement,
    destination
  }

  postReleaseCampaignEvent(
    RELEASE_CAMPAIGN_ANALYTICS_EVENTS.CTA_CLICKED,
    metadata
  )
}

export function resetReleaseCampaignViewDedupe() {
  if (import.meta.env.MODE === 'test') {
    viewedCampaignPlacements.clear()
    pendingCampaignPlacements.clear()
  }
}
